import { useState, useRef, useEffect } from 'react';
import { Play, Pause } from 'lucide-react';
import { Line } from 'rc-progress';
import { useSelector } from 'react-redux';
import { RootState } from 'store';

interface AudioCardProps {
  percent: number;
  url: string;
  role?: 'host' | 'guest';
}

const AudioCard = ({ percent, url, role = 'host' }: AudioCardProps) => {
  const [isMobile, setIsMobile] = useState(false); // to check for mobile

  useEffect(() => {
    if (window.innerWidth < 640) {
      setIsMobile(true);
    }
  }, [window.innerWidth]);
  const [isPlaying, setIsPlaying] = useState(false);
  const isDarkMode = useSelector((state: RootState) => state.theme.isDarkMode);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateProgress = () => {
      if (audio.duration) {
        setProgress((audio.currentTime / audio.duration) * 100);
      }
    };

    audio.addEventListener('timeupdate', updateProgress);
    audio.addEventListener('ended', () => setIsPlaying(false));

    return () => {
      audio.removeEventListener('timeupdate', updateProgress);
      audio.removeEventListener('ended', () => setIsPlaying(false));
    };
  }, []);

  const togglePlay = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  // Button background and text colors based on role
  const buttonBgColor = role === 'host' ? 'bg-[#444444]' : 'bg-[#f5f3ff]';
  const progressColor = role === 'host' ? '#FFFFFF' : '#846CEC';

  return (
    <div className="flex flex-row items-center gap-3 w-[320px] sm:w-full">
      {/* Play/Pause button */}
      <button
        className={`w-8 h-8 flex items-center justify-center ${buttonBgColor} rounded-lg`}
        onClick={togglePlay}
      >
        {isPlaying ? (
          <Pause color={role === 'host' ? "#FFFFFF" : "#846CEC"} size={18} />
        ) : (
          <Play color={role === 'host' ? "#FFFFFF" : "#846CEC"} size={18} />
        )}
      </button>

      {/* Progress bar */}
      <div className="w-full">
        <Line
          percent={isMobile ? progress : percent}
          strokeWidth={2}
          strokeColor={progressColor}
          trailColor={role === 'host' ? '#555555' : (isDarkMode ? '#F0EEFB' : 'lightgray')}
        />
      </div>

      {/* Audio element (hidden) */}
      <audio ref={audioRef} src={url} />
    </div>
  );
};

export default AudioCard;
